/**
 * CategoryManagement - 资产分类管理组件
 * 功能：超级管理员配置右侧抽屉的动态目录树
 * - 新增/编辑/删除分类
 * - 拖拽排序
 * - 区分工作流/资产库两种类型
 */

import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, GripVertical, Layers, Image, Film, Music, Save, Loader2, FileText } from 'lucide-react';
import api from '../api';
import AlertToast from './AlertToast';

const ICON_OPTIONS = [
  { value: 'photo', label: '图片', Icon: Image },
  { value: 'film', label: '视频', Icon: Film },
  { value: 'music', label: '音频', Icon: Music },
  { value: 'text', label: '文本', Icon: FileText },
  { value: 'layers', label: '工作流', Icon: Layers },
];

const SCOPE_OPTIONS = [
  { value: 'personal', label: '个人库' },
  { value: 'project', label: '团队库' },
  { value: 'global', label: '全局库' },
];

const TYPE_OPTIONS = [
  { value: 'workflow', label: '工作流分类（显示在上方）' },
  { value: 'library', label: '资产库分类（显示在下方）' },
];

export default function CategoryManagement({ embedded = false, onClose }) {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [toast, setToast] = useState({ message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: 'success' }), 3000);
  };

  // 加载分类列表
  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/asset-categories');
      const allCategories = data.categories || [];

      // 去重：按 category_key 分组，每组只保留一条（多库统一管理，只显示一条；
      // 图片/视频/音频库只有 personal+project 两条，文本/工作流库有 personal+project+global 三条）
      const uniqueMap = new Map();
      allCategories.forEach(cat => {
        const key = cat.category_key || `${cat.name}-${cat.type}`;
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, cat);
        }
      });

      setCategories(Array.from(uniqueMap.values()));
    } catch (err) {
      showToast('加载分类失败：' + (err.message || '未知错误'), 'warning');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  // 开始编辑
  const startEdit = (category) => {
    setEditingId(category.id);
    setEditForm({
      name: category.name,
      type: category.type,
      icon: category.icon,
      asset_types: category.asset_types || [], // 支持的媒体类型数组
      // scope: 新增时不需要，因为会在三个 scope 分别创建
    });
  };

  // 保存编辑
  const saveEdit = async (id) => {
    if (!editForm.name.trim()) {
      showToast('分类名称不能为空', 'warning');
      return;
    }

    try {
      // 找到当前分类，获取其 category_key
      const currentCategory = categories.find(c => c.id === id);
      if (!currentCategory?.category_key) {
        // 没有 category_key，只更新这一条
        await api.put(`/api/asset-categories/${id}`, editForm);
      } else {
        // 有 category_key，批量更新所有同 key 的库（personal+project，或 +global）
        const allCategories = await api.get('/api/asset-categories');
        const sameCategoryKeyItems = (allCategories.categories || []).filter(
          cat => cat.category_key === currentCategory.category_key
        );

        // 批量更新所有同 category_key 的分类
        await Promise.all(sameCategoryKeyItems.map(cat =>
          api.put(`/api/asset-categories/${cat.id}`, editForm)
        ));
      }

      showToast('保存成功');
      setEditingId(null);
      setEditForm(null);
      loadCategories();
      // 通知其他组件刷新
      window.dispatchEvent(new Event('categoriesUpdated'));
    } catch (err) {
      showToast('保存失败：' + (err.message || '未知错误'), 'warning');
    }
  };

  // 删除分类
  const handleDelete = async (id) => {
    if (!window.confirm('确定删除此分类？如有资产引用将无法删除。')) return;

    try {
      // 找到当前分类，获取其 category_key
      const currentCategory = categories.find(c => c.id === id);
      if (!currentCategory?.category_key) {
        // 没有 category_key，只删除这一条
        await api.delete(`/api/asset-categories/${id}`);
      } else {
        // 有 category_key，批量删除所有同 key 的库（personal+project，或 +global）
        const allCategories = await api.get('/api/asset-categories');
        const sameCategoryKeyItems = (allCategories.categories || []).filter(
          cat => cat.category_key === currentCategory.category_key
        );

        // 批量删除所有同 category_key 的分类
        await Promise.all(sameCategoryKeyItems.map(cat =>
          api.delete(`/api/asset-categories/${cat.id}`)
        ));
      }

      showToast('删除成功');
      loadCategories();
      window.dispatchEvent(new Event('categoriesUpdated'));
    } catch (err) {
      showToast('删除失败：' + (err.message || '未知错误'), 'warning');
    }
  };

  // 新增工作流
  const handleAddWorkflow = () => {
    const newId = 'new-' + Date.now();
    setCategories(prev => [...prev, {
      id: newId,
      name: '',
      category_key: '',
      type: 'workflow',
      icon: 'layers',
      scope: 'global',
      sort_order: Math.max(...prev.map(c => c.sort_order || 0), 0) + 100,
      is_system: false,
      asset_types: [],
    }]);
    startEdit({ id: newId, name: '', type: 'workflow', icon: 'layers', asset_types: [] });
  };

  // 新增资产库
  const handleAddLibrary = () => {
    const newId = 'new-' + Date.now();
    setCategories(prev => [...prev, {
      id: newId,
      name: '',
      category_key: '',
      type: 'library',
      icon: 'photo',
      scope: 'global',
      sort_order: Math.max(...prev.map(c => c.sort_order || 0), 0) + 100,
      is_system: false,
      asset_types: [],
    }]);
    startEdit({ id: newId, name: '', type: 'library', icon: 'photo', asset_types: [] });
  };

  // 保存新增
  const saveNew = async () => {
    if (!editForm.name.trim()) {
      showToast('分类名称不能为空', 'warning');
      return;
    }

    if (editForm.type === 'library' && (!editForm.asset_types || editForm.asset_types.length === 0)) {
      showToast('请至少选择一种媒体类型', 'warning');
      return;
    }

    try {
      // 自动生成 category_key（各 scope 共用同一个）
      const category_key = 'custom-' + Date.now();
      const payload = {
        name: editForm.name,
        type: editForm.type,
        icon: editForm.icon,
        category_key,
        asset_types: editForm.asset_types || [],
        sort_order: Math.max(...categories.map(c => c.sort_order || 0), 0) + 100,
        // 🆕 后端自动创建：图片/视频/音频库只建 personal+project（全局库不支持这些媒体上传），
        // 文本/工作流库仍建 personal+project+global（事务保证原子性）
        create_all_scopes: true
      };

      const result = await api.post('/api/asset-categories', payload);

      showToast(result.message || '新增成功');
      setEditingId(null);
      setEditForm(null);
      loadCategories();
      window.dispatchEvent(new Event('categoriesUpdated'));
    } catch (err) {
      showToast('新增失败：' + (err.message || '未知错误'), 'warning');
    }
  };

  // 拖拽排序
  const handleDragStart = (id) => {
    setDragId(id);
  };

  const handleDragOver = (e, targetId) => {
    if (dragId && dragId !== targetId) {
      e.preventDefault();
    }
  };

  const handleDrop = async (targetId) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }

    const newOrder = [...categories];
    const dragIndex = newOrder.findIndex(c => c.id === dragId);
    const targetIndex = newOrder.findIndex(c => c.id === targetId);

    if (dragIndex < 0 || targetIndex < 0) {
      setDragId(null);
      return;
    }

    const [removed] = newOrder.splice(dragIndex, 1);
    newOrder.splice(targetIndex, 0, removed);

    // 重新计算 sort_order
    const updatedCategories = newOrder.map((cat, idx) => ({
      ...cat,
      sort_order: idx * 100,
    }));

    setCategories(updatedCategories);
    setDragId(null);

    // 批量更新排序（同时更新所有同 category_key 的库）
    try {
      // 获取所有分类（包括不同 scope 的重复项）
      const allData = await api.get('/api/asset-categories');
      const allCategories = allData.categories || [];

      // 为每个更新的分类，找到所有同 category_key 的记录并更新
      const updatePromises = [];
      updatedCategories.forEach(cat => {
        const sameKeyCategories = allCategories.filter(
          c => c.category_key === cat.category_key
        );

        sameKeyCategories.forEach(sameKeyCat => {
          updatePromises.push(
            api.put(`/api/asset-categories/${sameKeyCat.id}`, {
              sort_order: cat.sort_order
            })
          );
        });
      });

      await Promise.all(updatePromises);
      showToast('排序已更新');
      window.dispatchEvent(new Event('categoriesUpdated'));
    } catch (err) {
      showToast('排序更新失败', 'warning');
      loadCategories(); // 失败时重新加载
    }
  };

  // 按类型分组
  const workflowCategories = categories.filter(c => c.type === 'workflow');
  const libraryCategories = categories.filter(c => c.type === 'library');

  const renderCategory = (cat) => {
    const isEditing = editingId === cat.id;
    const isNew = String(cat.id).startsWith('new-');
    const IconComponent = ICON_OPTIONS.find(opt => opt.value === (isEditing ? editForm.icon : cat.icon))?.Icon || Image;

    return (
      <div
        key={cat.id}
        draggable={!isEditing}
        onDragStart={() => !isEditing && handleDragStart(cat.id)}
        onDragOver={(e) => !isEditing && handleDragOver(e, cat.id)}
        onDrop={() => !isEditing && handleDrop(cat.id)}
        onDragEnd={() => setDragId(null)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: isEditing ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.03)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 8,
          opacity: dragId === cat.id ? 0.4 : 1,
          cursor: isEditing ? 'default' : 'grab',
        }}
      >
        {!isEditing && <GripVertical size={16} color="#64748b" style={{ flexShrink: 0 }} />}

        {isEditing ? (
          <>
            {/* 编辑表单 */}
            <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: '400px' }}>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="分类名称"
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 6,
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  boxSizing: 'border-box',
                }}
              />
              {/* 新增时不显示类型下拉框（按钮已决定类型），编辑时显示但禁用 */}
              {!isNew && (
                <select
                  value={editForm.type}
                  onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                  disabled={true} // 已有分类不能改类型
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 6,
                    color: 'var(--text-primary)',
                    fontSize: 12,
                  }}
                >
                  {TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}
              {/* 资产库分类显示媒体类型复选框 */}
              {editForm.type === 'library' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>
                    支持的媒体类型
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {ICON_OPTIONS.filter(opt => opt.value !== 'layers').map(opt => {
                      const isChecked = (editForm.asset_types || []).includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 10px',
                            background: isChecked ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${isChecked ? 'rgba(59,130,246,0.4)' : 'var(--border-subtle)'}`,
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 12,
                            color: isChecked ? '#60a5fa' : 'var(--text-secondary)',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            if (!isChecked) {
                              e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isChecked) {
                              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const newTypes = e.target.checked
                                ? [...(editForm.asset_types || []), opt.value]
                                : (editForm.asset_types || []).filter(t => t !== opt.value);
                              setEditForm({ ...editForm, asset_types: newTypes, icon: newTypes[0] || 'photo' });
                            }}
                            style={{ margin: 0, cursor: 'pointer' }}
                          />
                          <opt.Icon size={14} />
                          <span>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => isNew ? saveNew() : saveEdit(cat.id)}
              style={{
                padding: '6px 12px',
                background: '#3b82f6',
                border: 'none',
                borderRadius: 6,
                color: '#fff',
                fontSize: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Save size={14} /> 保存
            </button>
            <button
              onClick={() => {
                if (isNew) {
                  setCategories(prev => prev.filter(c => c.id !== cat.id));
                }
                setEditingId(null);
                setEditForm(null);
              }}
              style={{
                padding: '6px 12px',
                background: 'rgba(239,68,68,0.2)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 6,
                color: '#f87171',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              取消
            </button>
          </>
        ) : (
          <>
            {/* 显示模式 */}
            <IconComponent size={18} color="#64748b" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                {cat.name}
              </div>
              {cat.is_system && (
                <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>
                  系统分类
                </div>
              )}
              {/* 显示资产库支持的媒体类型 */}
              {cat.type === 'library' && cat.asset_types && cat.asset_types.length > 0 && (
                <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                  {cat.asset_types.map(type => {
                    const option = ICON_OPTIONS.find(opt => opt.value === type);
                    if (!option) return null;
                    return (
                      <div
                        key={type}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          padding: '2px 6px',
                          background: 'rgba(100,116,139,0.15)',
                          border: '1px solid rgba(100,116,139,0.3)',
                          borderRadius: 4,
                          fontSize: 10,
                          color: '#94a3b8',
                        }}
                      >
                        <option.Icon size={10} />
                        <span>{option.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              onClick={() => startEdit(cat)}
              style={{
                padding: '6px 10px',
                background: 'rgba(59,130,246,0.15)',
                border: '1px solid rgba(59,130,246,0.3)',
                borderRadius: 6,
                color: '#60a5fa',
                fontSize: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Edit2 size={12} /> 编辑
            </button>
            <button
              onClick={() => handleDelete(cat.id)}
              style={{
                padding: '6px 10px',
                background: 'rgba(239,68,68,0.15)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 6,
                color: '#f87171',
                fontSize: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Trash2 size={12} /> 删除
            </button>
          </>
        )}
      </div>
    );
  };

  // 内嵌面板内容（无遮罩层、无弹窗）
  const panelContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)' }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Layers size={18} style={{ color: '#6366f1' }} />
          资产分类配置
        </h3>
        <p style={{ margin: '4px 0 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
          配置右侧抽屉的资产分类目录树
        </p>
      </div>

      {/* Content */}
      <div
        className="custom-scrollbar"
        style={{
          flex: 1,
          marginTop: 16,
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            <div style={{ marginTop: 12, fontSize: 13 }}>加载中...</div>
          </div>
        ) : (
          <>
            {/* 说明 */}
            <div
              style={{
                fontSize: 11,
                color: '#64748b',
                lineHeight: 1.6,
                marginBottom: 18,
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 8,
              }}
            >
              工作流分类显示在上方，资产库分类显示在下方。拖拽调整顺序。
            </div>

            {/* 工作流分类 */}
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              >
                工作流分类（显示在上方）
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {workflowCategories.length === 0 ? (
                  <div
                    style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: 'var(--text-muted)',
                      fontSize: 12,
                    }}
                  >
                    暂无工作流分类
                  </div>
                ) : (
                  workflowCategories.map(renderCategory)
                )}
              </div>
            </div>

            {/* 资产库分类 */}
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              >
                资产库分类（显示在下方）
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {libraryCategories.length === 0 ? (
                  <div
                    style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: 'var(--text-muted)',
                      fontSize: 12,
                    }}
                  >
                    暂无资产库分类
                  </div>
                ) : (
                  libraryCategories.map(renderCategory)
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 12,
        }}
      >
        <button
          onClick={handleAddWorkflow}
          disabled={loading || editingId !== null}
          style={{
            padding: '8px 16px',
            background: 'rgba(99,102,241,0.15)',
            border: '1px solid rgba(99,102,241,0.3)',
            borderRadius: 8,
            color: '#a5b4fc',
            cursor: loading || editingId ? 'not-allowed' : 'pointer',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            opacity: loading || editingId ? 0.5 : 1,
          }}
        >
          <Plus size={16} /> 新增工作流
        </button>
        <button
          onClick={handleAddLibrary}
          disabled={loading || editingId !== null}
          style={{
            padding: '8px 16px',
            background: 'rgba(59,130,246,0.15)',
            border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: 8,
            color: '#60a5fa',
            cursor: loading || editingId ? 'not-allowed' : 'pointer',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            opacity: loading || editingId ? 0.5 : 1,
          }}
        >
          <Plus size={16} /> 新增资产库
        </button>
      </div>

      {toast.message && (
        <AlertToast message={toast.message} type={toast.type} position="center" />
      )}
    </div>
  );

  return panelContent;
}
